"use client";

import { ArrowDown, ArrowUp, TriangleAlert, X } from "lucide-react";
import { decodeAudioFile, durationSeconds, inspectAudio, type PcmAudio } from "mediatools";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import type { MediaOperation, RunContext } from "../_operations/types";
import { AudioResultPanel, type ProcessedAudio } from "./AudioResultPanel";
import { CaptionOverlay } from "./CaptionOverlay";
import type { CaptionPreviewSpec } from "./captions";
import { formatBytes } from "./format";
import { ImageResultPanel, type ProcessedImage } from "./ImageResultPanel";
import { MediaDropzone } from "./MediaDropzone";
import { acceptForKinds, mediaKindOf } from "./media";
import { UnsupportedBrowserNotice } from "./UnsupportedBrowserNotice";
import { useToolSupport } from "./useToolSupport";
import { type ProcessedVideo, VideoResultPanel } from "./VideoResultPanel";

type Result = ProcessedAudio | ProcessedVideo | ProcessedImage;

/** Friendly names for audio codecs browsers typically can't decode. */
const AUDIO_CODEC_LABELS: Record<string, string> = {
  ac3: "AC-3 (Dolby Digital)",
  eac3: "E-AC-3 (Dolby Digital Plus)",
  dts: "DTS",
};
const COMMON_ERROR_KEYS: Record<string, string> = {
  "No file selected.": "noFileSelected",
  "No file loaded.": "noFileLoaded",
  "Add at least two files to merge.": "addTwoFiles",
  "Load a video and let it load before capturing.": "loadVideoBeforeCapture",
  "Canvas is not available in this browser.": "canvasUnavailable",
  "Could not capture the frame.": "captureFailed",
};

/**
 * The shared shell that renders any {@link MediaOperation}: capability gate,
 * file load (decode-once / preview as the op requires), options form, action,
 * progress, and the right result panel. One component powers every tool's page.
 */
export function MediaWorkspace<Opts>({
  operation,
  initialFiles,
}: {
  operation: MediaOperation<Opts>;
  /** Pre-load these files on mount (used by the unified /studio surface). */
  initialFiles?: File[];
}) {
  const t = useTranslations("mediaTools");
  const support = useToolSupport(operation.id);
  const [files, setFiles] = useState<File[]>([]);
  const [decoded, setDecoded] = useState<PcmAudio | null>(null);
  const [duration, setDuration] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [captionPreview, setCaptionPreview] = useState<CaptionPreviewSpec | null>(null);
  // Set when the loaded file has audio the browser can't decode (e.g. AC-3) — the
  // output would be silent. Holds the codec for the message, or null when fine.
  const [undecodableAudio, setUndecodableAudio] = useState<string | null>(null);
  const [options, setOptions] = useState<Opts>(operation.defaultOptions);
  const [busy, setBusy] = useState<null | "loading" | "running">(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const previewRef = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const runOperation = useCallback(
    async (runFiles: File[], decodedAudio: PcmAudio | null, dur: number) => {
      if (runFiles.length === 0) return;
      setError(null);
      setResult(null);
      setProgress(0);
      setBusy("running");
      try {
        const ctx: RunContext = {
          files: runFiles,
          file: runFiles[0] ?? null,
          mediaKind: mediaKindOf(runFiles[0]),
          decoded: decodedAudio,
          duration: dur,
          previewUrl,
          onProgress: setProgress,
          getPreviewElement: () => previewRef.current,
        };
        setResult(await operation.run(ctx, options));
      } catch (cause) {
        if (cause instanceof Error) {
          const errorKey = COMMON_ERROR_KEYS[cause.message];
          setError(errorKey ? t(`errors.${errorKey}`) : cause.message);
        } else {
          setError(t("errors.generic"));
        }
      } finally {
        setBusy(null);
      }
    },
    [operation, options, previewUrl]
  );

  const handleFiles = async (incoming: File[]) => {
    setError(null);
    setResult(null);
    const next = operation.multiple ? [...files, ...incoming] : incoming.slice(0, 1);
    setFiles(next);

    const first = next[0];
    if (!first || operation.multiple) return;

    setCaptionPreview(null);
    setUndecodableAudio(null);
    if (operation.showSourcePreview) setPreviewUrl(URL.createObjectURL(first));

    // Warn (don't block) if the file's audio can't be decoded in the browser, so
    // a silent export isn't a surprise. Video only — audio ops surface a hard
    // error from the decode step instead. Fire-and-forget; metadata read is cheap.
    if (mediaKindOf(first) === "video") {
      void inspectAudio(first).then((info) => {
        if (info.hasAudio && !info.decodable) setUndecodableAudio(info.codec ?? "unknown");
      });
    }

    let dur = 0;
    let pcm: PcmAudio | null = null;
    if (operation.needsDecodedAudio) {
      setBusy("loading");
      setDecoded(null);
      setDuration(0);
      try {
        pcm = await decodeAudioFile(first);
        dur = durationSeconds(pcm);
        setDecoded(pcm);
        setDuration(dur);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : t("errors.couldNotReadFile"));
        setBusy(null);
        return;
      }
      setBusy(null);
    }

    if (operation.autoRun) await runOperation(next, pcm, dur);
  };

  // Pre-load files handed in by /studio (where the file is chosen before the tool).
  const loadedInitial = useRef(false);
  useEffect(() => {
    if (loadedInitial.current || !initialFiles?.length) return;
    loadedInitial.current = true;
    void handleFiles(initialFiles);
  }, [initialFiles]);

  const updateFiles = (nextFiles: File[]) => {
    setResult(null);
    setError(null);
    setFiles(nextFiles);
  };
  const moveFile = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= files.length) return;
    const next = [...files];
    [next[index], next[target]] = [next[target] as File, next[index] as File];
    updateFiles(next);
  };

  if (support.status === "checking") {
    return (
      <div className="flex items-center gap-2 text-foreground/70">
        <Spinner className="h-4 w-4" /> {t("workspace.checkingSupport")}
      </div>
    );
  }
  if (support.status === "unsupported") {
    return <UnsupportedBrowserNotice missing={support.missing} />;
  }

  const OptionsForm = operation.OptionsForm;
  const ctx = {
    files,
    file: files[0] ?? null,
    mediaKind: mediaKindOf(files[0]),
    decoded,
    duration,
    previewUrl,
  };
  const minFiles = operation.multiple ? 2 : 1;
  const ready = files.length >= minFiles && (!operation.needsDecodedAudio || decoded !== null);
  const operationKey = `operations.${operation.id}`;
  const dropPromptKey =
    operation.inputKinds.includes("audio") && operation.inputKinds.includes("video")
      ? "workspace.dropPrompt.audioVideo"
      : operation.inputKinds.includes("video")
        ? "workspace.dropPrompt.video"
        : "workspace.dropPrompt.audio";
  const audioCodecLabel = (codec: string) =>
    AUDIO_CODEC_LABELS[codec] ??
    (codec === "unknown" ? t("workspace.unknownAudioFormat") : codec.toUpperCase());

  return (
    <div className="space-y-6">
      <MediaDropzone
        accept={acceptForKinds(operation.inputKinds)}
        multiple={operation.multiple}
        prompt={t(dropPromptKey)}
        onDrop={handleFiles}
        disabled={busy !== null}
      />

      {!operation.multiple && ctx.file && (
        <p className="text-sm text-foreground/70">
          {t("workspace.selected")}{" "}
          <span className="font-medium text-foreground">{ctx.file.name}</span>{" "}
          <span className="text-foreground/50">({formatBytes(ctx.file.size)})</span>
        </p>
      )}

      {operation.multiple && files.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <p className="mb-3 text-sm text-foreground/70">
            {t("workspace.fileCount", { count: files.length })}
          </p>
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 rounded border px-3 py-2"
              >
                <span className="w-5 text-sm text-foreground/50">{index + 1}.</span>
                <span className="min-w-0 flex-1 truncate text-sm">
                  {file.name} <span className="text-foreground/50">({formatBytes(file.size)})</span>
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("workspace.moveUp")}
                  disabled={busy !== null || index === 0}
                  onClick={() => moveFile(index, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("workspace.moveDown")}
                  disabled={busy !== null || index === files.length - 1}
                  onClick={() => moveFile(index, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("workspace.remove")}
                  disabled={busy !== null}
                  onClick={() => updateFiles(files.filter((_, i) => i !== index))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {previewUrl &&
        (ctx.mediaKind === "video" ? (
          <div className="relative">
            <video
              ref={previewRef as React.RefObject<HTMLVideoElement>}
              src={previewUrl}
              controls
              className="max-h-[65vh] w-full rounded bg-black object-contain"
              onLoadedMetadata={(event) => {
                if (operation.needsDecodedAudio) return;
                const total = event.currentTarget.duration;
                if (Number.isFinite(total) && total > 0) setDuration(total);
              }}
            />
            {captionPreview && <CaptionOverlay videoRef={previewRef} spec={captionPreview} />}
          </div>
        ) : (
          <audio
            ref={previewRef as React.RefObject<HTMLAudioElement>}
            src={previewUrl}
            controls
            className="w-full"
          />
        ))}

      {undecodableAudio && (
        <p className="flex items-start gap-2 text-sm text-warning" role="status">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t("workspace.undecodableAudio", { codec: audioCodecLabel(undecodableAudio) })}
          </span>
        </p>
      )}

      {busy === "loading" && (
        <p className="flex items-center gap-2 text-sm text-foreground/70">
          <Spinner className="h-4 w-4" /> {t("workspace.analyzing")}
        </p>
      )}

      {OptionsForm && (
        <OptionsForm
          options={options}
          setOptions={setOptions}
          ctx={ctx}
          capabilities={support.report}
          disabled={busy !== null}
          getPreviewElement={() => previewRef.current}
          setCaptionPreview={setCaptionPreview}
        />
      )}

      {!operation.autoRun && (
        <Button
          onClick={() => runOperation(files, decoded, duration)}
          loading={busy === "running"}
          disabled={busy !== null || !ready}
        >
          {busy === "running"
            ? t(`${operationKey}.actionLabelBusy`)
            : t(`${operationKey}.actionLabel`)}
        </Button>
      )}

      {busy === "running" && operation.outputKind === "video" && (
        <Progress value={Math.round(progress * 100)} className="w-full" />
      )}

      {operation.autoRun && busy === "running" && (
        <p className="flex items-center gap-2 text-sm text-foreground/70">
          <Spinner className="h-4 w-4" /> {t("workspace.working")}
        </p>
      )}

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {result && operation.outputKind === "video" && (
        <VideoResultPanel
          result={result as ProcessedVideo}
          title={t(`${operationKey}.resultTitle`)}
        />
      )}
      {result && operation.outputKind === "image" && (
        <ImageResultPanel
          result={result as ProcessedImage}
          title={t(`${operationKey}.resultTitle`)}
        />
      )}
      {result && operation.outputKind === "audio" && (
        <AudioResultPanel
          result={result as ProcessedAudio}
          opusSupported={support.report.audioEncodeOpus}
          title={t(`${operationKey}.resultTitle`)}
        />
      )}
    </div>
  );
}
